import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CancelCrossIcon, SelectTrigger } from "@activecollab/components";
import classnames from "classnames";
import moment from "moment";
import PropTypes from "prop-types";
import { useSecurity } from "~/components/Hooks";
import { FETCH_TIME_RECORDS_SUCCESS } from "~/store/action_types/index.jsx";
import { fetchProject } from "~/store/actions/actions.projects.jsx";
import { fetchAllTaskLists } from "~/store/actions/actions.taskLists.jsx";
import { fetchPaginatedTasks } from "~/store/actions/actions.tasks.jsx";
import { fetchParentTimeRecords } from "~/store/actions/actions.timeRecords.jsx";
import { getConfigOption } from "~/store/selectors/selector.configOptions.jsx";
import { getProjectById } from "~/store/selectors/selector.projects.jsx";
import { getTaskById } from "~/store/selectors/selector.tasks.jsx";
import { makeGetByParentIdAndParentType } from "~/store/selectors/selector.timeRecords.jsx";
import { getLoggedUser, getUsersFromProjectMembersForUserSelector } from "~/store/selectors/selector.users.jsx";
import { descSortCompare } from "~/utils/utils.jsx";
import useAcSelectDate from "../Hooks/useAcSelectDate.jsx";
import usePagination from "../Hooks/usePagination.jsx";
import ProjectTaskMenuSelector from "../MenuSelector/ProjectTaskMenuSelector.jsx";
import SelectDateNonWorkingDays from "../SelectDate/SelectDateNonWorkingDays.jsx";
import TimeRecordFormWithList from "../TimeRecordForm/TimeRecordFormWithList.jsx";
import { TimeRecordModalContent } from "../Tracking/TimeRecordModalContent.jsx";

export const AddTimeRecordContent = ({ onClose, date, userId, hidePeoplePicker, source }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const can = useSecurity();
  const [loading, setLoading] = useState(false);
  const [headers, setHeaders] = useState({});
  const getParentTimeRecords = useMemo(makeGetByParentIdAndParentType, []);
  const taskTimeTracking = useSelector((state) => getConfigOption(state, "task_time_tracking_enabled"));
  const loggedUser = useSelector(getLoggedUser);
  const [taskId, setTaskId] = useState();
  const [projectId, setProjectId] = useState();
  const task = useSelector((state) => getTaskById(state, taskId));
  const project = useSelector((state) => getProjectById(state, projectId));
  const parentType = useMemo(() => {
    return taskId > 0 ? "Task" : "Project";
  }, [taskId]);
  const parentId = useMemo(() => {
    return taskId > 0 ? taskId : projectId;
  }, [projectId, taskId]);
  const parentTimeRecords = useSelector((state) => getParentTimeRecords(state, parentType, parentId));

  const users = useSelector((state) => getUsersFromProjectMembersForUserSelector(state, projectId));

  const canTrackTimeForOtherUsers = can(["can_track_time_for_others"], project);
  const backtrackMaxDays = useSelector((state) => getConfigOption(state, "backtrack_time_max_days"));
  const disableDaysBefore = canTrackTimeForOtherUsers ? null : moment().subtract(backtrackMaxDays, "days");

  const { dates, formatDates, handleDateChange } = useAcSelectDate(date ? { from: date, to: date } : null);

  const handleTaskSelect = useCallback((id) => {
    setTaskId(id);
  }, []);

  const handleProjectSelect = useCallback(
    (id) => {
      dispatch(fetchProject(id)).then(() => {
        if (!taskTimeTracking) {
          handleTaskSelect(0, id);
        } else {
          dispatch(fetchPaginatedTasks(id, { status: "open" }));
          dispatch(fetchAllTaskLists(id));
        }
        setProjectId(id);
      });
    },
    [taskTimeTracking, dispatch, handleTaskSelect]
  );

  const handleAction = useCallback((action) => {
    if (action.type === FETCH_TIME_RECORDS_SUCCESS) {
      setHeaders(action.responseHeaders ? action.responseHeaders : {});
    }
  }, []);

  const pagination = usePagination(headers);

  const getTimeRecords = useCallback(
    async (cursor) => {
      if (!parentType || !parentId) {
        return;
      }

      let query = {};
      if (cursor) {
        query = { ...query, cursor };
      }
      if (formatDates.from && formatDates.to) {
        query = { ...query, from: formatDates.from, to: formatDates.to };
      }
      const action = await dispatch(fetchParentTimeRecords(parentType, parentId, projectId, query));
      setLoading(false);
      handleAction(action);
    },
    [parentType, parentId, formatDates.from, formatDates.to, dispatch, projectId, handleAction]
  );

  const handleFetchMore = useCallback(() => {
    if (pagination?.cursor && !loading) {
      setLoading(true);
      getTimeRecords(pagination.cursor);
    }
  }, [pagination.cursor, loading, getTimeRecords]);

  useEffect(() => {
    getTimeRecords();
  }, [getTimeRecords]);

  const filteredUsers = useMemo(() => {
    let result = canTrackTimeForOtherUsers && !hidePeoplePicker ? users : [];
    if (result.length === 1 && users[0].id === loggedUser.id) {
      result = [];
    }
    return result;
  }, [canTrackTimeForOtherUsers, loggedUser.id, hidePeoplePicker, users]);

  const showFormElements = useMemo(() => {
    return projectId > 0 && taskId >= 0;
  }, [projectId, taskId]);

  const timeRecords = useMemo(() => {
    return parentTimeRecords
      .filter((record) => {
        return (
          !record.is_trashed &&
          moment.unix(record.record_date).utc().format(`YYYY-MM-DD`) === formatDates.to &&
          record.parent_type === parentType &&
          record.parent_id === parentId
        );
      })
      .sort((a, b) => {
        return descSortCompare(a.created_on, b.created_on);
      })
      .map((record) => ({
        id: record.id,
        jobTypeId: record.job_type_id,
        userId: record.user_id,
        value: record.value,
        recordDate: record.record_date,
        parentType: record.parent_type,
        parentId: record.parent_id,
        summary: record.summary,
        billableStatus: record.billable_status,
        createdOn: record.created_on,
        userName: record.user_name,
        userEmail: record.user_email,
      }));
  }, [parentTimeRecords, formatDates.to, parentId, parentType]);

  const backToSelectProject = useCallback(() => {
    setProjectId(undefined);
    setTaskId(undefined);
  }, []);

  return (
    <TimeRecordModalContent
      isLoadingMore={loading}
      showLimitedAccess={!pagination?.cursor}
      handleFetchMore={handleFetchMore}
    >
      <div className="time-record-form-modal">
        <div
          className={classnames(`tw-flex tw-justify-between`, {
            "tw-items-start": !showFormElements,
            "tw-items-center": showFormElements,
          })}
        >
          {showFormElements ? (
            <div className="tw-flex tw-items-center tw-truncate tw-mr-4">
              <div className="tw-text-base tw-truncate">
                {taskId < 1 ? (
                  <>
                    <span className="tw-italic">{t("Project")}: </span>
                    <span className="tw-font-bold">{project.name}</span>
                  </>
                ) : (
                  <span className="tw-font-bold">{task.name}</span>
                )}
              </div>
              <CancelCrossIcon
                onClick={backToSelectProject}
                height={12}
                width={12}
                className="tw-mx-2 tw-cursor-pointer tw-stroke-current tw-shrink-0"
              />
            </div>
          ) : (
            <div>
              <ProjectTaskMenuSelector
                onProjectSelect={handleProjectSelect}
                onTaskSelect={handleTaskSelect}
                onBack={backToSelectProject}
              />
            </div>
          )}
          <SelectDateNonWorkingDays
            trigger={(text) => (
              <SelectTrigger
                disabled={!projectId}
                className="tw-w-auto"
              >
                {text}
              </SelectTrigger>
            )}
            selectedDays={dates}
            selectionMode="daily"
            onSave={handleDateChange}
            required
            type="selectable_global_day_off"
            weekendIsSelectable
            disableDaysBefore={disableDaysBefore}
            position="bottom-end"
          />
        </div>
        {showFormElements ? (
          <>
            <div className="tw-mb-8 tw-mt-5 tw-border-solid tw-border-0 tw-border-b tw-border-border-primary" />
            <TimeRecordFormWithList
              projectId={projectId}
              isBillable={taskId > 0 && task ? task.is_billable : project.is_billable}
              onCancel={onClose}
              source={source}
              to={formatDates.to}
              from={formatDates.from}
              parentId={taskId > 0 ? taskId : projectId}
              parentType={taskId > 0 ? "Task" : "Project"}
              taskId={taskId > 0 ? taskId : null}
              loggedUserId={userId ?? loggedUser.id}
              onDismiss={onClose}
              timeRecords={timeRecords}
              users={filteredUsers}
              defaultUserId={userId ?? loggedUser.id}
              defaultUserName={loggedUser.display_name}
            />
          </>
        ) : null}
      </div>
    </TimeRecordModalContent>
  );
};

AddTimeRecordContent.propTypes = {
  onClose: PropTypes.func,
  date: PropTypes.any,
  userId: PropTypes.number,
  hidePeoplePicker: PropTypes.bool,
  source: PropTypes.string,
};
